import { useMemo, useState } from 'react';
import { useQuery } from '@apollo/client';
import { styled } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { GET_TAM_PRIMES } from '@bloomays-lib/adapter.api-talent';
import { format } from 'date-fns/format';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '../../stores';
import { errorLogger } from '../../helpers/error';
import { GetTamPrimesQueryData } from '../../types/primes';
import { mapAnnualTamPrimeToTableRows, getAnnualCurrentMonthHeaderState } from '../../utils/annualPrime.utils';
import { getTamDisplayName } from '../../utils/monthlyPrime.utils';
import AnnualTamPrimesHeader from '../molecules/AnnualTamPrimesHeader';
import AnnualTamPrimesTable from '../molecules/AnnualTamPrimesTable';

export type AnnualTamPrimesProps = {
  tamEmail?: string;
  tamDisplayName?: string;
  referenceDate?: Date;
};

const AnnualTamPrimes = ({
  tamEmail: tamEmailOverride,
  tamDisplayName: tamDisplayNameOverride,
  referenceDate: referenceDateOverride,
}: AnnualTamPrimesProps) => {
  const { t } = useTranslation(['primes']);
  const auth = useAuthStore((state) => state.auth);
  const authTamEmail = typeof auth?.user?.email === 'string' ? auth.user.email.trim() : '';
  const isTamPreview = Boolean(tamEmailOverride);
  const tamEmail = isTamPreview ? tamEmailOverride?.trim() : authTamEmail;
  const tamDisplayName = tamDisplayNameOverride?.trim() || getTamDisplayName(auth?.user);
  const initialYear = referenceDateOverride ? format(referenceDateOverride, 'yyyy') : format(new Date(), 'yyyy');
  const [selectedYear, setSelectedYear] = useState(() => initialYear);

  const { loading, error, data } = useQuery<GetTamPrimesQueryData>(GET_TAM_PRIMES, {
    skip: !tamEmail,
    variables: {
      tamEmail: tamEmail,
      year: selectedYear,
    },
    context: { clientName: 'api.talents' },
    fetchPolicy: 'network-only',
  });

  const aggregate = data?.getTamPrimes;
  const tableRows = useMemo(() => (aggregate ? mapAnnualTamPrimeToTableRows(aggregate) : []), [aggregate]);
  const currentMonth = useMemo(() => (aggregate ? getAnnualCurrentMonthHeaderState(aggregate) : null), [aggregate]);

  if (!tamEmail) {
    return <ErrorMessage variant="body1">{t('missingTamEmail')}</ErrorMessage>;
  }

  if (error) {
    errorLogger(error, { extraInfos: 'getTamPrimes failed on /primes/annuel' });
    return <ErrorMessage variant="body1">{t('annualLoadError')}</ErrorMessage>;
  }

  return (
    <Container>
      <AnnualTamPrimesHeader
        tamDisplayName={tamDisplayName}
        tamSeniority={aggregate?.tamSeniority}
        selectedYear={selectedYear}
        onSelectedYearChange={setSelectedYear}
        currentMonth={currentMonth}
        loading={loading}
      />
      <AnnualTamPrimesTable rows={tableRows} loading={loading} />
    </Container>
  );
};

export default AnnualTamPrimes;

const Container = styled('div')(({ theme }) => ({
  width: '100%',
  maxWidth: '100%',
  boxSizing: 'border-box',
  overflowX: 'hidden',
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(3),
  [theme.breakpoints.down('sm')]: {
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
  },
}));

const ErrorMessage = styled(Typography)(({ theme }) => ({
  marginTop: theme.spacing(3),
  color: theme.palette.error.main,
}));
